import type { TabsProps } from 'antd';

import {
  CloseOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  SwapOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Modal, Tabs, Spin, Button } from '@bancobarigui/barihaus';

import { InspectionInfo } from './InspectionInfo';
import { InspectionLocation } from './InspectionLocation';
import { InspectionSchedules } from './InspectionSchedules';
import { useDetailInspection } from './useDetailInspection';
import { InspectionAssignments } from './InspectionAssignments';
import { InspectionIssues } from './InspectionIssues';
import { LaudoTab, PreliminaryCalculationsTab } from '@/features/laudo';
import { CalculatorOutlined } from '@ant-design/icons';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { InspectionVideoCommunication } from './InspectionVideoCommunication';
import { CancelInspectionModal } from '@/components/CancelInspectionModal';
import { InspectionStatus, InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { useVideoSessionStatus } from '@/shared/hooks/useVideoSessionStatus';
import { useEffect, useState } from 'react';

interface DetailInspectionProps {
  open: boolean;
  inspectionId: string | null;
  itemId?: string | null;
  onClose: () => void;
  onUpdate?: () => void;
}

export function DetailInspection({ open, inspectionId, itemId, onClose, onUpdate }: DetailInspectionProps) {
  const { inspection, isLoading, activeTab, setActiveTab, refreshInspection } = useDetailInspection({
    inspectionId,
    itemId,
    isOpen: open,
  });
  const { isBankUser, hasAnyProviderRole, hasAnyBankRole } = usePermissions();

  // Flag para indicar que a televistoria acabou de ser finalizada (para auto-switch para Laudo)
  const [justFinishedTelevistoria, setJustFinishedTelevistoria] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Verifica se a inspeção está com status "Agendada" para mostrar a aba de Vídeo
  const isAgendada = inspection?.status === String(InspectionStatus.Agendada);

  // Verificar se houve chamada de vídeo completada usando hook customizado
  const { hasCompletedVideoCall } = useVideoSessionStatus(inspection?.items, {
    enabled: inspection !== null && open,
  });

  // NOVO FLUXO - Visibilidade da aba de Laudo por role:
  // - Inspetor: vê quando status = 20 (televistoria finalizada) para preencher checklist
  //   Ou quando status >= 13 para ver o laudo gerado pela Prestadora (somente visualização)
  // - Prestadora: vê a partir do status 13 (AguardandoRevisaoPrestadora - checklist preenchido)
  // - Banco: vê a partir do status 15 (AguardandoRevisaoBanco - laudo gerado)
  const itemStatus = inspection?.items?.[0]?.status;
  // Garantir que itemStatus é número para comparação (pode vir como string da API)
  const itemStatusNum = itemStatus !== undefined && itemStatus !== null
    ? (typeof itemStatus === 'string' ? parseInt(itemStatus, 10) : itemStatus)
    : undefined;

  // Aba de Laudo aparece se:
  // 1. Teve televistoria finalizada (hasCompletedVideoCall) OU
  // 2. Tem checklist preenchido (status >= 13 = AguardandoRevisaoPrestadora)
  const canSeeLaudoTab = (() => {
    // Se houve televistoria finalizada, mostrar aba
    if (hasCompletedVideoCall) {
      return true;
    }

    // Se tem checklist preenchido (status >= 13), mostrar aba
    if (itemStatusNum !== undefined && !isNaN(itemStatusNum)) {
      if (itemStatusNum >= InspectionItemStatus.AguardandoRevisaoPrestadora) {
        return true;
      }
    }

    return false;
  })();

  const canSeePreliminaryCalc = !canSeeLaudoTab && (
    hasAnyBankRole() || (
      hasAnyProviderRole() && itemStatusNum !== undefined && itemStatusNum > InspectionItemStatus.AguardandoAceitePrestadora
    )
  );

  // Auto-switch para aba Laudo quando televistoria acabou de ser finalizada
  useEffect(() => {
    if (justFinishedTelevistoria && canSeeLaudoTab) {
      setActiveTab('laudo');
      setJustFinishedTelevistoria(false);
    }
  }, [justFinishedTelevistoria, canSeeLaudoTab, setActiveTab]);

  // Handler para quando a televistoria é finalizada
  const handleVideoSessionUpdate = () => {
    setJustFinishedTelevistoria(true);
    refreshInspection();
    onUpdate?.();
  };

  // Handler genérico para atualizar dados (chama refresh interno e externo)
  const handleInspectionUpdate = () => {
    refreshInspection();
    onUpdate?.();
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'info',
      label: (
        <span className="flex items-center gap-2">
          <InfoCircleOutlined />
          Informações
        </span>
      ),
      children: inspection ? <InspectionInfo inspection={inspection} onUpdate={handleInspectionUpdate} /> : null,
    },
    {
      key: 'location',
      label: (
        <span className="flex items-center gap-2">
          <EnvironmentOutlined />
          Localização
        </span>
      ),
      children: inspection ? <InspectionLocation inspection={inspection} onUpdate={handleInspectionUpdate} /> : null,
    },
    {
      key: 'assignments',
      label: (
        <span className="flex items-center gap-2">
          <SwapOutlined />
          Direcionamentos
        </span>
      ),
      children: inspection ? <InspectionAssignments inspection={inspection} /> : null,
    },
    {
      key: 'schedules',
      label: (
        <span className="flex items-center gap-2">
          <CalendarOutlined />
          Agendamentos
        </span>
      ),
      children: inspection ? <InspectionSchedules inspection={inspection} onUpdate={handleInspectionUpdate} /> : null,
    },
    {
      key: 'issues',
      label: (
        <span className="flex items-center gap-2">
          <ExclamationCircleOutlined />
          Pendências
        </span>
      ),
      children: inspection ? <InspectionIssues inspection={inspection} /> : null,
    },
    // Aba de Vídeo e Comunicação só aparece quando status é "Agendada"
    ...(!isAgendada ? [{
      key: 'video',
      label: (
        <span className="flex items-center gap-2">
          <VideoCameraOutlined />
          Vídeo e Comunicação
        </span>
      ),
      children: inspection ? <InspectionVideoCommunication inspection={inspection} onUpdate={handleVideoSessionUpdate} /> : null,
    }] : []),
    // Aba de Laudo aparece para Inspetor, Prestadora e Banco
    ...(canSeeLaudoTab ? [{
      key: 'laudo',
      label: (
        <span className="flex items-center gap-2">
          <FileTextOutlined />
          Laudo
        </span>
      ),
      children: inspection ? <LaudoTab inspection={inspection} onRefresh={handleInspectionUpdate} /> : null,
    }] : []),
    
    ...(canSeePreliminaryCalc ? [{
      key: 'memorial',
      label: (
        <span className="flex items-center gap-2">
          <CalculatorOutlined />
          Memorial Preliminar
        </span>
      ),
      children: inspection ? <PreliminaryCalculationsTab inspection={inspection} /> : null,
    }] : []),
  ];

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      width={1200}
      footer={inspection && isBankUser() && itemStatusNum !== InspectionItemStatus.Cancelado && itemStatusNum !== InspectionItemStatus.Frustrado ? (
        <div className="flex justify-end">
          <Button
            icon={<StopOutlined />}
            onClick={() => setIsCancelModalOpen(true)}
            style={{ borderColor: '#101010', color: '#101010', backgroundColor: 'transparent', boxShadow: 'none' }}
          >
            Cancelar Inspeção
          </Button>
        </div>
      ) : null}
      closeIcon={<CloseOutlined />}
      className="inspection-detail-modal"
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" />
        </div>
      ) : inspection ? (
        <>
          <div className="mb-6 pr-8">
            <h2 className="text-2xl font-bold">Inspeção #{inspection.proposalNumber}</h2>
            <p className="text-gray-500">Visualize todos os detalhes da inspeção</p>
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="inspection-tabs"
          />
        </>
      ) : null}

      <CancelInspectionModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onSuccess={() => {
          setIsCancelModalOpen(false);
          handleInspectionUpdate();
        }}
        inspectionData={inspection ?? undefined}
      />
    </Modal>
  );
}
